// ADO #334. An agreement may not be submitted while a serial number is listed twice on it, or is
// already on another live agreement. The server decides both, authoritatively, on submit
// (jbwservicetitansagreementspull findEquipmentConflicts); this is the same rule for the one half
// the dialog can answer by itself, so a duplicate is caught before the save round trip.
//
// Keep the normalization in step with that Lambda: a serial the dialog lets through and the
// server then refuses reads as the dialog contradicting itself. The wording differs on purpose:
// the Lambda writes a one-line summary to Comments, and this dialog carries the full guidance.

// Trimmed, case-insensitive. A blank or missing serial is never a conflict -- equipment
// regularly arrives from ServiceTitan without one (#151, #187). N/A and NA count as blank: dealers
// must type something, so thermostats routinely carry "n/a".
const PLACEHOLDER_SERIALS = new Set(["N/A", "NA"]);
export const normalizeSerial = (serial) => {
  const key = serial == null ? "" : String(serial).trim().toUpperCase();
  return PLACEHOLDER_SERIALS.has(key) ? "" : key;
};

// A row with a manufacturer: not null or missing, not the string "null", not blank. Update-mode
// handleSave keeps only these, and the duplicate check skips the rest -- a serial on a row that is
// never sent cannot be a duplicate on the agreement. Create mode drops only null manufacturers, so
// a duplicate on a blank-manufacturer row there is left to the server's check.
export const hasManufacturer = (row) =>
  row.manufacturer != null &&
  row.manufacturer !== "null" &&
  String(row.manufacturer).trim().length > 0;

// Each serial listed more than once, quoted as it was first typed (trimmed).
export const findDuplicateSerials = (rows) => {
  const shown = new Map();
  const duplicates = [];
  for (const row of rows || []) {
    if (!hasManufacturer(row)) continue;
    const key = normalizeSerial(row.serialNumber);
    if (!key) continue;
    if (!shown.has(key)) {
      shown.set(key, String(row.serialNumber).trim());
    } else if (!duplicates.some((d) => normalizeSerial(d.serial) === key)) {
      duplicates.push({ serial: shown.get(key) });
    }
  }
  return duplicates;
};

// Worded per #198: what is wrong, and whether the dealer can fix it or support has to.
export const conflictMessages = ({ duplicates = [], covered = [] } = {}) => [
  ...duplicates.map(
    (d) =>
      `Serial number ${d.serial} is listed more than once on this agreement. Remove or correct the duplicate row, then submit again.`
  ),
  ...covered.map(
    (c) =>
      `Serial number ${c.serial} is already covered by agreement ${c.jbwAgreementId}. Check the serial number. If agreement ${c.jbwAgreementId} should not cover this unit, contact JB Warranties support.`
  ),
];

// The 409 body the submit Lambda returns for a conflict, or null for any other failure.
// overrideRefused: an override was asked for and the server did not grant it.
export const conflictFromError = (err) => {
  if (err?.response?.status !== 409) return null;
  const { duplicates = [], covered = [], overrideRefused = false } = err.response.data || {};
  return { duplicates, covered, overrideRefused };
};

// The submit Lambda answers 403 when the caller may not submit this agreement (not their own, or
// no session it recognises). Worded per #198: not something the dealer can fix on this screen.
export const SUBMIT_FORBIDDEN_MESSAGE =
  "You don't have permission to submit this agreement. If you think you should, contact JB Warranties support.";
export const isSubmitForbidden = (err) => err?.response?.status === 403;
